import React, { createContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { isBillingError, isUnpaid } from '../utils/billing';
import { areEqual } from '../utils/iterable/areEqual';
import { getSize } from '../utils/iterable/getSize';
import { shops } from '../utils/selectors';
import { toJS } from '../utils/iterable/toJS';
import { get } from '../utils/iterable/get';

import { BETA_CHANNELS } from '../constants/features';
import { EMPTY } from '../constants';
import { PLAN } from '../constants/billing';

const OK_VALUE = { error: false, unpaid: false };
export const SubscriptionContext = createContext(OK_VALUE);
SubscriptionContext.displayName = 'SubscriptionContext';

function subscriptionSelector(state) {
  const shop = shops.currentShop(state);

  if (!shop || BETA_CHANNELS.has(shop.get('channel')) || shop.get('isVelaOnlyShop')) {
    return EMPTY.MAP;
  }

  return state.getIn(['user', 'subscriptions', shops.currentShopId(state)]);
}

function syncStatusSelector(state) {
  const shop = shops.currentShop(state);

  return shop && shop.get('syncStatus');
}

export default function SubscriptionProvider({ children }) {
  const errorsRef = useRef({});
  const currentShopId = useSelector(shops.currentShopId);
  const shopsById = useSelector(shops.shopsById, areEqual);
  const userHasShops = useSelector(shops.userHasShops);
  const userLoaded = useSelector(get(['user', 'loaded']));
  const subscriptions = useSelector(get(['user', 'subscriptions'])) || EMPTY.MAP;
  const subscription = useSelector(subscriptionSelector, areEqual);
  const syncStatus = useSelector(syncStatusSelector, areEqual);
  const value = useMemo(
    function factory() {
      if (!userLoaded || !userHasShops) {
        if (getSize(errorsRef.current) > 0) {
          errorsRef.current = {};
        }

        return OK_VALUE;
      }

      if (subscription === EMPTY.MAP) {
        return OK_VALUE;
      }

      if (!subscription) {
        if (!errorsRef.current[currentShopId]) {
          errorsRef.current[currentShopId] = true;
          Sentry.captureException(
            'Subscription not found',
            {
              extra: {
                shops: toJS(shopsById),
                subscriptions: toJS(subscriptions),
              },
            }
          );
        }

        return { error: true, unpaid: false };
      }

      const error = isBillingError({ plan: subscription.get('plan', PLAN.ERROR), syncStatus });
      const unpaid = !error && isUnpaid(subscription);

      return { error, unpaid };
    },
    [currentShopId, subscription, syncStatus, userHasShops, userLoaded]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

SubscriptionProvider.propTypes = {
  children: PropTypes.node,
};
